import React from 'react';
import PropTypes from 'prop-types';
import { useDarkMode } from '../contexts/DarkModeContext';
import { LinkService } from '../Services/LinkService';
import sessionTracker from '../Utils/SessionTracker.js';
import MetaTagManager from '../components/SEO/MetaTagManager';
import { BRANDING } from '../config/branding';
import '../CSS/DarkMode.css';

export default function MediumStyleArticle(props) {
  const { title, subtitle, author, authorProfileImage, date, readTime, children, originalUrl, slug, refreshButton } = props;
  useDarkMode(); // This hook applies dark mode context

  // Generate description from title/subtitle for meta tags
  const generateDescription = () => {
    const baseDescription = subtitle || title || 'AI Conversation Summary';
    const truncated = baseDescription.length > 160 
      ? baseDescription.substring(0, 157) + '...' 
      : baseDescription;
    return `${truncated} | Shared on ${BRANDING.WEBSITE_NAME}`;
  };

  // Generate full URL for this summary
  const summaryUrl = `${BRANDING.WEBSITE_URL}/summary/${slug}`;

  // Create a high-quality social sharing image URL
  const socialImageUrl = `${BRANDING.WEBSITE_URL}${BRANDING.SOCIAL_IMAGE}`;

  // Function to handle click tracking for "View Original Conversation" link
  const handleOriginalLinkClick = async (e) => {
    if (slug) {
      try {
        console.log('=== ORIGINAL LINK CLICK TRACKING START ===');
        console.log('🔗 Tracking click for View Original Conversation link');
        console.log('   Slug:', slug);
        console.log('   BASE_URL:', 'https://trackable-gpt-backend.onrender.com/');
        console.log('   Full endpoint URL:', `https://trackable-gpt-backend.onrender.com/links/summary-clicks`);
        
        // Get current session data from SessionTracker
        const sessionData = sessionTracker.getSessionData();
        console.log('🔑 Current session data for original link click:', {
          sessionId: sessionData.sessionId,
          visitorId: sessionData.visitorId,
          sessionStart: sessionData.sessionStart?.toISOString(),
          sessionEnd: sessionData.sessionEnd?.toISOString(),
          isActive: sessionTracker.isSessionActive()
        });
        
        const clickData = { 
          slug, 
          action: 'original_link_click',
          sessionId: sessionData.sessionId,
          visitorId: sessionData.visitorId,
          sessionStart: sessionData.sessionStart?.toISOString(),
          sessionEnd: sessionData.sessionEnd?.toISOString()
        };
        console.log('   Sending data:', clickData);
        
        const response = await LinkService.logSummaryClick(clickData);
        console.log('✅ Summary original link click logged successfully');
        console.log('   Response:', response);
        console.log('=== ORIGINAL LINK CLICK TRACKING END ===');
      } catch (error) {
        console.log('=== ORIGINAL LINK CLICK ERROR START ===');
        console.error('❌ Failed to log summary click:', error);
        console.error('   Error message:', error.message);
        console.error('   Error status:', error.response?.status);
        console.error('   Error data:', error.response?.data);
        console.error('   Network error?', !error.response);
        console.log('=== ORIGINAL LINK CLICK ERROR END ===');
        // Don't prevent the link from working even if tracking fails
      }
    } else {
      console.warn('⚠️ No slug available for original link click tracking');
    }
  };


  return (
    <>
      {/* SEO Meta Tags for Rich Link Previews */}
      <MetaTagManager
        title={`${title} | ${BRANDING.WEBSITE_NAME}`}
        description={generateDescription()}
        image={socialImageUrl}
        url={summaryUrl}
        author={author}
        publishedTime={date}
        readTime={readTime}
      />
      
      <main 
        className={`font-serif text-[1.125rem] leading-7 summary-article`}
        style={{ 
          backgroundColor: 'var(--bg-primary)', 
          color: 'var(--text-primary)',
          minHeight: '100vh',
          width: '100%',
          margin: 0,
          padding: 0
        }}
      >
      <article 
        className="max-w-4xl mx-auto py-12"
        style={{
          padding: '3rem clamp(1rem, 8vw, 4rem)',
          backgroundColor: 'var(--bg-primary)',
          minHeight: 'auto'
        }}
      >
        <h1 
          className="text-4xl font-bold leading-tight mb-4 text-center"
          style={{ color: 'var(--text-primary)' }}
        >
          {title}
        </h1>
        
        {/* Refresh Button - positioned after title */}
        {refreshButton && (
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            marginBottom: '16px' 
          }}>
            {refreshButton}
          </div>
        )}
        
        {subtitle && (
          <h2 
            className="text-lg italic mb-6 text-center subtitle"
            style={{ color: 'var(--text-secondary)' }}
          >
            {subtitle}
          </h2>
        )}

        {/* Author Information Section */}
        <div 
          className="mb-8" 
          style={{ 
            padding: '0',
            marginBottom: '2rem'
          }}
        >
          <div 
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '1.5rem',
              flexWrap: 'wrap'
            }}
          >
            {/* Avatar */}
            <div 
              style={{
                width: '48px',
                height: '48px',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                overflow: 'hidden',
                background: 'linear-gradient(135deg, #3b82f6, #9333ea)',
                position: 'relative'
              }}
            >
              {authorProfileImage && (
                <img 
                  src={authorProfileImage} 
                  alt={`${author || 'Author'}'s profile`}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    borderRadius: '50%',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    zIndex: 2
                  }}
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              )}
              <span 
                style={{
                  color: 'white',
                  fontWeight: '600',
                  fontSize: '18px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '100%',
                  height: '100%',
                  zIndex: 1
                }}
              >
                {(author && typeof author === 'string' && author.length > 0) ? author.charAt(0).toUpperCase() : 'A'}
              </span>
            </div>
            
            {/* Author Name */}
            <div 
              style={{ 
                color: 'var(--text-primary)',
                fontWeight: '500',
                fontSize: '16px',
                whiteSpace: 'nowrap'
              }}
            >
              {author || 'Anonymous'}
            </div>
            
            {/* Read Time and Date */}
            <div 
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                fontSize: '14px',
                color: 'var(--text-secondary)',
                whiteSpace: 'nowrap'
              }}
            >
              <span>{readTime || '5 min read'}</span>
              <span>•</span>
              <span>{date}</span>
            </div>
            
            {/* View Original Conversation Link */}
            {originalUrl && (
              <a
                href={originalUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={handleOriginalLinkClick}
                style={{ 
                  color: 'var(--link-color)',
                  transition: 'color 0.2s ease',
                  fontSize: '14px',
                  textDecoration: 'underline',
                  whiteSpace: 'nowrap'
                }}
                onMouseEnter={(e) => e.target.style.color = 'var(--link-hover)'}
                onMouseLeave={(e) => e.target.style.color = 'var(--link-color)'}
              >
                View Original Conversation ↗
              </a>
            )}
          </div>
        </div>

        <div className="prose prose-lg max-w-none mx-auto pb-8" style={{ padding: '0' }}>
          {children}
        </div>
      </article>
    </main>
    </>
  );
}

MediumStyleArticle.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  author: PropTypes.string,
  authorProfileImage: PropTypes.string,
  date: PropTypes.string.isRequired,
  readTime: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  originalUrl: PropTypes.string,
  slug: PropTypes.string,
};
